import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Alert, Modal, SelectProps, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import produce from 'immer';
import Form from '~/components/WizardForm';
import { useGetApplication, useGetPayload, useSubmitStep } from '~/controllers/wizard';
import { canSubmitCompleteReview } from '~/api/wizard';
import { Stage } from '~/types/Application';
import { Draft } from '~/types/Draft';
import buildNamePath from '~/utils/buildNamePath';
import { DocumentInput, Select } from '~/components';
import Section from '~/layouts/Section ';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import StepReviewComment from '~/pages/ksa/review/components/StepReviewComment';
import ReviewResult from '~/pages/ksa/review/completeReview/components/ReviewResult';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import createUid from '~/utils/createUid';
import { getDecodedJwt } from '~/utils/getDecodedJwt';

const CompleteReview: FC = () => {
  const { t } = useTranslation('ksaReview');

  const [form] = Form.useForm();

  const applicationQuery = useGetApplication();
  const submitMutation = useSubmitStep();
  const payload = useGetPayload();

  const reviewResult = payload?.completeReview ?? [];
  const newReviewIndex = reviewResult.length;

  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const isProgrammaticClear = React.useRef(false);

  const decisionValue = Form.useWatch(n(newReviewIndex)('decision'), form);
  const decodedToken = getDecodedJwt();

  // Clears the decision field and shows a validation error
  const handleCannotSubmit = (reason?: string) => {
    isProgrammaticClear.current = true;
    form.setFields([{ name: n(newReviewIndex)('decision'), value: undefined }]);
    setErrorMessage(reason || t('Previous steps must be submitted and valid before submitting completeReview'));
  };

  // When agent selects APPROVED, verify all previous steps are complete via API
  // Note: screeningNeedsRetrigger is handled separately — Approve is hidden from the dropdown
  useEffect(() => {
    const checkCanSubmit = async () => {
      if (decisionValue === 'APPROVED') {
        try {
          setErrorMessage('');
          const response = await canSubmitCompleteReview();
          if (!response.canSubmit) {
            handleCannotSubmit(response.reason);
          }
        } catch (error) {
          console.error('Error checking canSubmit:', error);
          handleCannotSubmit();
        }
      } else if (!isProgrammaticClear.current) {
        setErrorMessage('');
      } else {
        isProgrammaticClear.current = false;
      }
    };
    checkCanSubmit();
  }, [decisionValue, form, newReviewIndex]);

  useEffect(() => {
    const hasFlags = hasAtLeastOneFlagOnReview(payload);
    const decisionValue = form.getFieldValue(n(newReviewIndex)('decision'));
    if ((decisionValue === 'APPROVED' && hasFlags) || (decisionValue === 'RESUBMIT' && !hasFlags)) {
      form.setFields([{ name: n(newReviewIndex)('decision'), value: undefined }]);
    }
  }, [form, newReviewIndex, payload]);

  if (!applicationQuery.data) return null;

  return (
    <Container className="CompleteReview">
      <Form<Draft<'completeReview'>>
        stage={Stage.ksaReview}
        payloadKey="completeReview"
        form={form}
        onBeforeFinish={(result) => {
          Modal.confirm({
            title: t('Complete Application review '),
            content: (
              <Typography.Paragraph>
                <p>{t('Would you like to complete application review and submit your results?')}</p>
              </Typography.Paragraph>
            ),
            okText: t('Proceed', { ns: 'common' }),
            cancelText: t('Cancel', { ns: 'common' }),
            icon: null,
            onOk() {
              const patchedResult = produce(result, (draft) => {
                draft.completeReview[newReviewIndex].date = dayjs().toISOString();
                draft.completeReview[newReviewIndex].agentName = decodedToken?.name;
              });
              return submitMutation.mutateAsync(patchedResult);
            },
          });
          return Promise.reject();
        }}
      >
        <Typography.Title>{t('Complete review')}</Typography.Title>
        <Typography.Paragraph>
          {t(
            'Please examine the required information prior to determining whether to approve or decline the application of the client.'
          )}
        </Typography.Paragraph>

        {/* Show error if previous steps aren't complete */}
        {errorMessage && (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        )}

        {!submitMutation.isLoading && reviewResult?.map((record, index) => (
          <ReviewResult key={record.uid} record={record} nameFn={n(index)} />
        ))}
        {!applicationQuery.data?.readOnly &&
          !reviewResult.some(r => r.decision === 'APPROVED') && (
            <Section formItemFirst>
              <Form.Item name={n(newReviewIndex)('uid')} hidden initialValue={createUid()} />
              <Form.Item name={n(newReviewIndex)('files')}>
                <DocumentInput multiple={false} name="photos" variant="default" />
              </Form.Item>
              <InputsGroupLayout>
                <Form.Item
                  style={{ width: 300 }}
                  label={t('Decision on the application')}
                  name={n(newReviewIndex)('decision')}
                >
                  <DecisionReviewSelect />
                </Form.Item>
              </InputsGroupLayout>
              <StepReviewComment n={n(newReviewIndex)} />
            </Section>
          )}
      </Form>
    </Container>
  );
};

const hasAtLeastOneFlagOnReview = (payload: ApplicationPayload | undefined): boolean => {
  const payloadWithReviewKeys = _.pickBy(payload, (__, key) =>
    key.toLowerCase().includes('review')
  );

  const findDeep: typeof _.find = (obj: any, fn: any) =>
    _.find(obj, (val, key) => {
      if (_.isArray(val)) return findDeep(val, fn);
      if (_.isPlainObject(val)) return findDeep(val, fn);
      return fn(val, key);
    });

  return !_.isEmpty(findDeep(payloadWithReviewKeys, (v) => v === true));
};

const DecisionReviewSelect: FC<SelectProps<string>> = ({
  ...props
}) => {
  const { t } = useTranslation('ksaReview');
  const payload = useGetPayload();
  const hasFlags = useMemo(() => hasAtLeastOneFlagOnReview(payload), [payload]);

  // screeningNeedsRetrigger is passed down from CompleteReview — no duplicate API call needed
  const options = [
    // Hide Approve if there are flags or if screening needs to be re-triggered
    ...(!hasFlags
      ? [{ label: t('Approve'), value: 'APPROVED', key: 'APPROVED' }]
      : []),
    ...(!hasFlags ? [] : [{ label: t('Resubmit'), value: 'RESUBMIT', key: 'RESUBMIT' }]),
    { label: t('Decline'), value: 'DECLINED', key: 'DECLINED' },
  ];

  return <Select options={options} allowClear {...props} />;
};

const n = (i: number) => buildNamePath(['completeReview', i]);

const Container = styled.div``;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.5;
`;

export default CompleteReview;
